import type { ColumnDef, SortingState } from '@tanstack/react-table';
import cx from 'clsx';
import { fromQuerystring, POST, parseFormValues, toQuerystring, truncate } from 'common/helpers.ts';
import { isEqual } from 'es-toolkit';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { Alert } from 'types/Alert.ts';
import { Button } from 'ui/component/Button.tsx';
import { ISearch } from 'ui/component/Icons.tsx';
import { Tab } from 'ui/component/Tab.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { Input } from 'ui/control/Input.tsx';
import { AlertTag } from '#admin/component/AlertTag.tsx';
import { Datefrom } from '#admin/component/Datefrom.tsx';
import { ArchiveDialog } from './ArchiveDialog.tsx';
import { getSWRKeyForPage, Table } from './table/index.ts';

const getColumns = (t, onArchive: (id: string, shipmentId: string) => void): ColumnDef<Alert>[] => {
  const columns: ColumnDef<Alert>[] = [
    {
      accessorKey: 'shipment_logistics_ex_id',
      header: t('components.alerts.columns.orderNumber'),
      minSize: 190,
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return (
          <Link className="anchor anchor-novisit flex items-center gap-1 uppercase" to={`/shipment/${info.row.original.shipment_id}`}>
            <span className="w-28 overflow-hidden text-ellipsis whitespace-nowrap">{value}</span>
          </Link>
        );
      },
    },
    {
      accessorKey: 'package_logistics_ex_id',
      header: t('components.alerts.columns.packageId'),
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return (
          <Link className="anchor anchor-novisit flex items-center gap-1 uppercase" to={`/shipment/${info.row.original.shipment_id}`}>
            {value}
          </Link>
        );
      },
    },
    {
      accessorKey: 'tracker_id',
      header: t('components.alerts.columns.trackerId'),
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return (
          <Link className="anchor anchor-novisit flex items-center gap-1" to={`/tracker/${info.row.original.tracker_id}`}>
            {truncate(value, 8, '')}
          </Link>
        );
      },
    },
    {
      accessorKey: 'leg_id',
      header: t('components.alerts.columns.legId'),
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return (
          <Link className="anchor anchor-novisit flex items-center gap-1 uppercase" to={`/shipment/${info.row.original.shipment_id}`}>
            {truncate(value, 8, '')}
          </Link>
        );
      },
    },
    {
      header: t('components.alerts.columns.datetime'),
      accessorKey: 'generated_at',
      minSize: 180,
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return <Datefrom date={value} />;
      },
    },
    {
      header: t('components.alerts.columns.alert'),
      minSize: 200,
      enableSorting: false,
      cell: (info) => {
        const alert = info.row.original;
        return <AlertTag alert={alert} />;
      },
    },
    {
      header: t('components.alerts.columns.value'),
      accessorKey: 'value',
      minSize: 80,
      meta: { truncate: true },
      cell: (info) => {
        const value = info.getValue<string>();
        const alert = info.row.original;
        if (!value || alert.type === 'delay') return null;
        return `${value} ${alert.unit}`;
      },
    },
    {
      header: t('components.alerts.columns.threshold'),
      enableSorting: false,
      minSize: 80,
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        const alert = info.row.original;
        switch (alert.type) {
          case 'battery':
            return `${alert.min || '-'} ${alert.unit}`;
          case 'delay':
            return null;
          default:
            return `${alert.min || '-'} ${alert.unit} / ${alert.max || '-'} ${alert.unit}`;
        }
      },
    },
    {
      header: t('components.alerts.columns.reason'),
      accessorKey: 'archived_reason',
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        return <Tooltip text={value}>{value}</Tooltip>;
      },
    },
    {
      header: t('components.alerts.columns.actions'),
      cell: (info) => {
        const id = info.row.original.id;
        const shipmentId = info.row.original.shipment_id;
        const isArchived = info.row.original.archived_at;
        if (!id) return null;
        return (
          <Button disabled={!!isArchived} small={true} className="blue" onClick={() => onArchive(id, shipmentId)}>
            {t('components.alerts.buttons.archive')}
          </Button>
        );
      },
    },
  ];

  return columns;
};

type FormValues = {
  search: string;
  archived: boolean | '';
};

type Props = {
  shipmentId?: string;
  showColumns?: string[];
  className?: string;
  hideSearch?: boolean;
};

export const Alerts: React.FC<Props> = ({ shipmentId, className, showColumns, hideSearch = false }) => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'generated_at', desc: true }]);
  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState<string | null>(null);

  const defaultValues: FormValues = { search: '', archived: false };
  const defaultValuesWithSearchValues = React.useMemo(
    () => ({
      ...defaultValues,
      ...fromQuerystring(searchParams),
      ...(shipmentId ? { shipment_id: shipmentId } : {}),
    }),
    [searchParams, shipmentId],
  );

  const [values, setValues] = React.useState<FormValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState, reset, getValues } = formContext;

  const swr = useSWRInfinite(getSWRKeyForPage('/alert/list', 25, values, sorters, parseFormValues), POST);
  const { data: rowsCount } = useSWR(formState.isValid ? ['/alert/count', values, parseFormValues] : null, POST);

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset(defaultValues);
    triggerSubmit();
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
  };

  const onChangeTab = React.useCallback(
    (nextTab: string | number) => {
      const archived = String(nextTab) === 'archived';
      formContext.setValue('archived', archived);
      triggerSubmit();
    },
    [formContext, triggerSubmit],
  );

  const onArchive = React.useCallback((id: string) => {
    setArchiveTarget(id);
    setArchiveDialogOpen(true);
  }, []);

  React.useEffect(() => {
    reset(defaultValuesWithSearchValues);
    triggerSubmit();
  }, [reset, triggerSubmit, defaultValuesWithSearchValues]);

  const handleArchiveSuccess = React.useCallback(() => {
    setArchiveTarget(null);
    setArchiveDialogOpen(false);
    // Invalidate both the list and count queries
    swr.mutate();
    if (rowsCount) mutate(['/alert/count', values, parseFormValues]);
  }, [swr, rowsCount, values]);

  const handleCloseArchiveDialog = React.useCallback(() => {
    setArchiveTarget(null);
    setArchiveDialogOpen(false);
  }, []);

  const columns = React.useMemo(() => getColumns(t, onArchive), [t, onArchive]);

  const defaultTab = getValues('archived');

  // we can't rely only on formState.isDirty because it's true when defaultValues provided to useForm are changed, but we may have filters set by default provided in the URL
  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, getValues());

  return (
    <section className={cx('paper !p-6 mb-6 bg-white', className)}>
      <Tab
        tabs={{ active: t('components.alerts.tabs.active'), archived: t('components.alerts.tabs.archived') }}
        defaultTab={defaultTab ? 'archived' : 'active'}
        onChange={onChangeTab}
      />
      {archiveTarget && <ArchiveDialog id={archiveTarget} open={archiveDialogOpen} onClose={handleCloseArchiveDialog} onSuccess={handleArchiveSuccess} />}
      <FormProvider {...formContext}>
        <form
          id="search-form"
          ref={formRef}
          className={cx('flex flex-col gap-8', hideSearch ? 'my-4' : 'my-8')}
          noValidate={true}
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          {!hideSearch && (
            <>
              <div className="flex place-content-between place-items-center">
                <Input
                  className="w-[500px]"
                  name={'search'}
                  placeholder={t('components.alerts.search.placeholder')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />
              </div>
              <div className={cx('flex flex-col gap-6 bg-blue-sky p-4', !showFilters && 'hidden')}>
                <div className="flex flex-row gap-6" />
                <div className="flex flex-row gap-4">
                  <Button className={'blue min-w-max max-w-max'} type="button" onClick={toggleFilters}>
                    {t('components.alerts.buttons.close')}
                  </Button>
                  <Button className={'blue ml-auto min-w-max max-w-max'} type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                    {t('components.alerts.buttons.clearFilters')}
                  </Button>
                  <Button type="submit" className="blue-outlined min-w-max max-w-max" disabled={showFilters && !formState.isDirty}>
                    {t('components.alerts.buttons.apply')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      </FormProvider>

      <Table
        className={cx('max-h-[calc(100vh-395px)]')}
        columns={showColumns ? columns?.filter((column) => column.header && showColumns.includes(column.header.toString())) : columns}
        swr={swr}
        pagination={true}
        rowsCount={rowsCount}
        defaultSorting={sorters}
        onColumnSort={setSorters}
        estimateSize={50}
        overscan={50}
      />
    </section>
  );
};
